/**
 * CompactOrderCard — Enterprise-grade card for kanban swim lanes.
 * Collapsed: stacked-card visual showing items underneath.
 * Expanded: clean item table. "Mark ▾" dropdown for status transitions.
 */
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../config/currency';
import { formatOrderId, formatRelativeTime } from '../utils/orderFormatter';

interface CompactOrderCardProps {
  order: {
    id: number;
    status: string;
    createdAt: string;
    orderNumber?: string;
    customOrderId?: string;
    items: {
      itemName?: string;
      menuItem?: { name: string };
      quantity: number;
      price: number;
      comment?: string;
      portionName?: string;
      requestedSpiceLevel?: string;
      spiceLevel?: string;
      toppings?: { name: string }[] | string[];
    }[];
    total?: number;
    finalTotal?: number;
    customerName?: string;
    user?: { username: string } | null;
    orderNotes?: string;
    tableName?: string;
    floorName?: string;
    sectionName?: string;
    orderType?: string;
    qrCode?: { code: string } | string | null;
    isStaffOrder?: boolean;
    placedByName?: string;
  };
  theme: any;
  onClick: () => void;
  getBadgeStyle: (status: string) => React.CSSProperties;
  onStatusChange?: (orderId: number, newStatus: string) => void;
  onNavigateTable?: (qrCode: string) => void;
  onHighlightTable?: (tableName: string | null) => void;
  isUpdating?: boolean;
  /** Persistent inline error from the last failed status/money action on this order. */
  actionError?: string | null;
  otherStatuses?: string[];
  isHighlighted?: boolean;
  isDimmed?: boolean;
}

const STATUS_ACCENT: Record<string, string> = {
  PENDING: '#C4956E',
  ACCEPTED: '#2D6CB5',
  PREPARING: '#2D6CB5',
  READY: '#3DA06A',
  SERVED: '#8FA683',
  COMPLETED: '#A0A7AF',
  CANCELLED: '#C55A5A',
};

const STATUS_TRANSITIONS: Record<string, string[]> = {
  PENDING: ['ACCEPTED', 'PREPARING', 'READY', 'SERVED', 'COMPLETED', 'CANCELLED'],
  ACCEPTED: ['PREPARING', 'READY', 'SERVED', 'COMPLETED', 'CANCELLED'],
  PREPARING: ['READY', 'SERVED', 'COMPLETED', 'CANCELLED'],
  READY: ['SERVED', 'COMPLETED', 'CANCELLED'],
  SERVED: ['COMPLETED'],
  COMPLETED: [],
  CANCELLED: [],
};

const STATUS_LABELS: Record<string, { short: string; color: string }> = {
  ACCEPTED: { short: 'Accept', color: '#4A7FAF' }, /* basil-todo */
  PREPARING: { short: 'Prepare', color: '#4A7FAF' }, /* basil-todo */
  READY: { short: 'Ready', color: '#2D8A56' }, /* basil-todo */
  SERVED: { short: 'Served', color: '#6B8A5E' }, /* basil-todo */
  COMPLETED: { short: 'Done', color: '#7A818A' }, /* basil-todo */
  CANCELLED: { short: 'Cancel', color: '#B04040' }, /* basil-todo */
};

const CompactOrderCard: React.FC<CompactOrderCardProps> = ({
  order, theme, onClick, getBadgeStyle, onStatusChange, onNavigateTable, onHighlightTable,
  isUpdating, actionError, otherStatuses, isHighlighted, isDimmed,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [markAsOpen, setMarkAsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const accent = STATUS_ACCENT[order.status] || '#888';
  const total = order.finalTotal ?? order.total ?? order.items.reduce((s, i) => s + i.price * i.quantity, 0);
  const itemCount = order.items.reduce((s, i) => s + i.quantity, 0);
  const qrCode = typeof order.qrCode === 'string' ? order.qrCode : order.qrCode?.code;
  const shortId = formatOrderId(order.customOrderId || `#${order.id}`, 'vendor');
  const validTransitions = STATUS_TRANSITIONS[order.status] || [];

  useEffect(() => {
    if (!markAsOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setMarkAsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [markAsOpen]);

  const firstTwo = order.items.slice(0, 2).map(i => {
    const name = i.itemName || i.menuItem?.name || 'Item';
    return i.quantity > 1 ? `${name} ×${i.quantity}` : name;
  });
  const remaining = order.items.length - 2;
  const preview = firstTwo.join(', ') + (remaining > 0 ? ` +${remaining}` : '');

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'relative',
        zIndex: markAsOpen ? 50 : 'auto',
        opacity: isDimmed ? 0.3 : 1,
        transition: 'opacity 0.2s, transform 0.2s',
        transform: isHighlighted ? 'scale(1.01)' : 'scale(1)',
        marginBottom: 0,
      }}
    >
      {/* No stacked effect — each card is a single order */}

      {/* Main card */}
      <div
        className="w-full rounded-lg flex flex-col transition-shadow duration-150"
        style={{
          backgroundColor: theme.components?.card?.background || '#FFFFFF',
          border: `1px solid ${isHighlighted ? theme.primary + '60' : (theme.gray?.[200] || '#E5E5E3')}`,
          borderLeft: `3.5px solid ${accent}`,
          boxShadow: isHighlighted
            ? `0 0 0 1px ${theme.primary}30, 0 2px 8px rgba(0,0,0,0.08)`
            : '0 1px 3px rgba(0,0,0,0.04)',
        }}
        onMouseEnter={(e) => { if (!isHighlighted) e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; }}
        onMouseLeave={(e) => { if (!isHighlighted) e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}
      >
        {/* Header row — touch-friendly sizing (min 44px tap targets) */}
        <div
          className="flex items-center gap-2.5 px-3.5 py-3 cursor-pointer"
          onClick={() => setExpanded(p => !p)}
        >
          <div className="flex-1 min-w-0">
            {/* Order ID + table + time */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[13px] font-semibold" style={{ color: theme.text }}>
                {shortId}
              </span>
              {order.tableName && (
                <span className="inline-flex items-center">
                  <span
                    className="text-[11px] font-medium px-1.5 py-0.5 rounded-l"
                    style={{
                      backgroundColor: isHighlighted ? theme.primary : theme.gray?.[100] || '#F3F4F6',
                      color: isHighlighted ? '#fff' : theme.gray?.[600] || '#555',
                      cursor: 'pointer',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onHighlightTable) onHighlightTable(isHighlighted ? null : order.tableName!);
                    }}
                    title="Spotlight table"
                  >
                    T{order.tableName}
                  </span>
                  <span
                    className="text-[10px] px-1 py-0.5 rounded-r"
                    style={{
                      backgroundColor: theme.gray?.[100] || '#F3F4F6',
                      color: theme.gray?.[400] || '#999',
                      cursor: 'pointer',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (qrCode && onNavigateTable) onNavigateTable(qrCode);
                    }}
                    title="Go to table"
                  >
                    ↗
                  </span>
                  {otherStatuses && otherStatuses.length > 0 && otherStatuses.map(s => (
                    <span
                      key={s}
                      className="w-2 h-2 rounded-full ml-0.5"
                      style={{ backgroundColor: STATUS_ACCENT[s] || '#999' }}
                      title={`Also ${s.toLowerCase()}`}
                    />
                  ))}
                </span>
              )}
              {!order.tableName && order.orderType === 'TAKEAWAY' && (
                <span className="text-[11px] font-medium px-1.5 py-0.5 rounded" style={{ backgroundColor: theme.gray?.[100] || '#F3F4F6', color: theme.gray?.[500] || '#888' }}>
                  TW
                </span>
              )}
              <span className="text-[11px]" style={{ color: theme.gray?.[400] || '#bbb' }}>
                {formatRelativeTime(order.createdAt)}
              </span>
            </div>
            {/* Items preview */}
            <div className="text-[12px] mt-0.5 truncate" style={{ color: theme.gray?.[500] || '#888' }}>
              {preview}
            </div>
          </div>

          {/* Right: total + mark + chevron */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="text-right">
              <div className="text-[13px] font-bold" style={{ color: theme.text }}>{formatCurrency(total)}</div>
              <div className="text-[10px]" style={{ color: theme.gray?.[400] }}>{itemCount} items</div>
            </div>

            {validTransitions.length > 0 && onStatusChange && (
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setMarkAsOpen(p => !p); }}
                  disabled={isUpdating}
                  className="u-focus-ring text-[12px] font-medium px-3 py-2 rounded-lg whitespace-nowrap inline-flex items-center justify-center gap-1.5"
                  style={{
                    backgroundColor: 'transparent',
                    color: theme.gray?.[600] || '#555',
                    border: `1px solid ${theme.gray?.[300] || '#D1D5DB'}`,
                    opacity: isUpdating ? 0.5 : 1,
                    cursor: isUpdating ? 'wait' : 'pointer',
                    minHeight: 44,
                  }}
                  aria-busy={isUpdating || undefined}
                >
                  {isUpdating ? <><span className="u-btn-spinner" aria-hidden="true" />Updating…</> : 'Mark ▾'}
                </button>

                {markAsOpen && (
                  <div
                    className="absolute right-0 mt-1 rounded-lg shadow-xl border py-1 min-w-[150px]"
                    style={{
                      backgroundColor: theme.components?.card?.background || '#fff',
                      borderColor: theme.gray?.[200] || '#E5E5E3',
                      zIndex: 100,
                    }}
                  >
                    {validTransitions.map(status => {
                      const info = STATUS_LABELS[status];
                      if (!info) return null;
                      return (
                        <button
                          key={status}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMarkAsOpen(false);
                            onStatusChange(order.id, status);
                          }}
                          className="u-focus-ring w-full text-left px-4 py-2.5 text-[13px] font-medium transition-colors"
                          style={{ color: info.color, minHeight: 44 }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = theme.gray?.[50] || '#FAFAF8'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                          {info.short}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {expanded
              ? <ChevronUpIcon className="w-5 h-5 flex-shrink-0" style={{ color: theme.gray?.[400] }} />
              : <ChevronDownIcon className="w-5 h-5 flex-shrink-0" style={{ color: theme.gray?.[400] }} />
            }
          </div>
        </div>

        {/* Persistent inline error from the last failed action (cleared on retry/success) */}
        {actionError && (
          <div
            role="alert"
            className="px-3.5 pb-2 text-[11px] font-medium"
            style={{ color: 'var(--status-bad)' }}
          >
            {actionError}
          </div>
        )}

        {/* Expanded: item list */}
        {expanded && (
          <div className="px-3.5 pb-3" style={{ borderTop: `1px solid ${theme.gray?.[100] || '#F3F3F0'}` }}>
            <table className="w-full" style={{ borderCollapse: 'collapse' }}>
              <tbody>
                {order.items.map((item, idx) => {
                  const name = item.itemName || item.menuItem?.name || 'Item';
                  const extras: string[] = [];
                  if (item.portionName && item.portionName !== 'REGULAR') extras.push(item.portionName.toLowerCase());
                  const spice = item.requestedSpiceLevel || item.spiceLevel;
                  if (spice) extras.push(spice.toLowerCase());
                  if (item.toppings?.length) {
                    const tNames = item.toppings.map(t => typeof t === 'string' ? t : t.name);
                    extras.push(tNames.join(', '));
                  }
                  return (
                    <tr key={idx} style={{ borderBottom: idx < order.items.length - 1 ? `1px solid ${theme.gray?.[50] || '#FAFAF8'}` : 'none' }}>
                      <td className="py-1.5 pr-2" style={{ width: 28, verticalAlign: 'top' }}>
                        <span className="text-[12px] font-medium" style={{ color: theme.gray?.[400] }}>{item.quantity}×</span>
                      </td>
                      <td className="py-1.5">
                        <span className="text-[13px]" style={{ color: theme.text }}>{name}</span>
                        {extras.length > 0 && (
                          <span className="text-[11px] ml-1" style={{ color: theme.gray?.[400] }}>({extras.join(', ')})</span>
                        )}
                        {item.comment && (
                          <div className="text-[11px] italic mt-px" style={{ color: theme.warning || '#D97706' }}>"{item.comment}"</div>
                        )}
                      </td>
                      <td className="py-1.5 text-right" style={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                        <span className="text-[12px]" style={{ color: theme.gray?.[500] }}>{formatCurrency(item.price * item.quantity)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {order.orderNotes && (
              <div className="mt-2 text-[11px] px-2 py-1.5 rounded" style={{ backgroundColor: theme.gray?.[50] || '#FAFAF8', color: theme.gray?.[600] || '#666' }}>
                Note: {order.orderNotes}
              </div>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onClick(); }}
              className="mt-2.5 w-full text-[12px] font-medium py-2.5 rounded-lg"
              style={{ color: theme.gray?.[600] || '#555', backgroundColor: theme.gray?.[50] || '#FAFAF8', border: `1px solid ${theme.gray?.[200] || '#E5E5E3'}`, cursor: 'pointer', minHeight: 40 }}
            >
              View Full Details
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompactOrderCard;
